import * as React from "react";
import { 
  Backdrop, 
  CircularProgress, 
  Skeleton, 
  Box, 
  Stack 
} from "@mui/material";

export default function Loading({ 
  open, 
  type = "backdrop", 
  variant = "table",
  rows = 16,
  columns = 5,
  height = "75vh",
  codebgcolor = "grey.900",
  codecolor = "grey.600"
}) {
  // Small helper for table column widths
  const getColumnWidth = (colIndex) => [80, 200][colIndex] || 120;

  // Original backdrop loading
  if (type === "backdrop") {
    return (
      <Backdrop
        sx={{ color: "#fff", zIndex: (theme) => theme.zIndex.drawer + 1 }}
        open={open}
      >
        <CircularProgress color="inherit" />
      </Backdrop>
    );
  }

  // Skeleton loading variants
  if (type === "skeleton" && open) {
    switch (variant) {
      case "table":
        return (
          <Box sx={{ 
            width: "100%", 
            height, 
            p: 2,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center"
          }}>
            <Box sx={{ width: "100%" }}>
              <Stack spacing={3} sx={{ mb: 2, alignItems: "center" }}>
                <Skeleton variant="rounded" width="100%" height={30} animation="wave" />
                <Skeleton variant="rounded" width="100%" height={30} animation="wave" />
              </Stack>
              <Stack direction="row" spacing={10} sx={{ alignItems: "center" }}>
                <Skeleton variant="rounded" width="100%" height={30} animation="wave" />
                <Skeleton variant="rounded" width="100%" height={30} animation="wave" />
                <Skeleton variant="rounded" width="100%" height={30} animation="wave" />
              </Stack>
              {/* Table header skeleton */}
              <Stack direction="row" spacing={2} sx={{ mb: 2, justifyContent: "center" }}>
                {Array.from(new Array(columns)).map((_, colIndex) => (
                  <Skeleton 
                    key={colIndex} 
                    variant="rectangular" 
                    height={40} 
                    animation="wave"
                  />
                ))}
              </Stack>
              
              {/* Table rows skeleton */}
              {Array.from(new Array(rows)).map((_, rowIndex) => (
                <Stack key={rowIndex} direction="row" spacing={2} sx={{ mb: 1.5, justifyContent: "center" }}>
                  {Array.from(new Array(columns)).map((_, colIndex) => (
                    <Skeleton 
                      key={colIndex} 
                      variant="rectangular" 
                      width= "100%"
                      height={52} 
                      animation="wave"
                    />
                  ))}
                </Stack>
              ))}
            </Box>
          </Box>
        );

      case "card":
        return (
          <Box sx={{ width: "100%", p: 2 }}>
            {Array.from(new Array(rows)).map((_, index) => (
              <Box key={index} sx={{ mb: 3, p: 2, border: 1, borderColor: 'grey.200', borderRadius: 1 }}>
                <Stack spacing={1}>
                  <Skeleton variant="text" width="60%" height={32} animation="wave" />
                  <Skeleton variant="text" width="40%" height={24} animation="wave" />
                  <Skeleton variant="rectangular" width="100%" height={60} animation="wave" />
                  <Stack direction="row" spacing={1}>
                    <Skeleton variant="rectangular" width={80} height={32} animation="wave" />
                    <Skeleton variant="rectangular" width={80} height={32} animation="wave" />
                  </Stack>
                </Stack>
              </Box>
            ))}
          </Box>
        );

      case "form":
        return (
          <Box sx={{ width: "100%", maxWidth: 600, mx: "auto", p: 2 }}>
            <Stack spacing={3}>
              <Skeleton variant="text" width="50%" height={40} animation="wave" />
              <Skeleton variant="rectangular" width="100%" height={56} animation="wave" />
              <Skeleton variant="rectangular" width="100%" height={56} animation="wave" />
              <Skeleton variant="rectangular" width="70%" height={56} animation="wave" />
              <Skeleton variant="rectangular" width="100%" height={120} animation="wave" />
              <Stack direction="row" spacing={2} sx={{ mt: 2 }}>
                <Skeleton variant="rectangular" width={100} height={40} animation="wave" />
                <Skeleton variant="rectangular" width={100} height={40} animation="wave" />
              </Stack>
            </Stack>
          </Box>
        );

      case "list":
        return (
          <Box sx={{ width: "100%", p: 2 }}>
            {Array.from(new Array(rows)).map((_, index) => (
              <Stack key={index} direction="row" spacing={2} sx={{ mb: 2, alignItems: "center" }}>
                <Skeleton variant="circular" width={40} height={40} animation="wave" />
                <Box sx={{ flex: 1 }}>
                  <Skeleton variant="text" width="80%" height={20} animation="wave" />
                  <Skeleton variant="text" width="60%" height={16} animation="wave" />
                </Box>
                <Skeleton variant="rectangular" width={80} height={32} animation="wave" />
              </Stack>
            ))}
          </Box>
        );

      case "code":
        return (
          <Box sx={{ width: "58%", p: 2, mt: 5 }}>
            <Stack spacing={2} sx={{ mb: 2, alignItems: "center" }}>
                <Skeleton variant="rounded" width="100%" height={30} animation="wave" />
                <Skeleton variant="rounded" width="100%" height={30} animation="wave" />
              </Stack>
              <Stack spacing={40} sx={{ mb: 2, alignItems: "center" }}>
                <Skeleton variant="rounded" width="50%" height={30} animation="wave" />
                {/* <Skeleton variant="rounded" width="100%" height={40} animation="wave" /> */}
              </Stack>
            <Box
              sx={{
                bgcolor: codebgcolor,
                borderRadius: 2,
                p: 2,
                fontFamily: "monospace",
                overflow: "hidden",
                height: "60vh",
              }}
            >
              
              <Stack spacing={1}>
                {Array.from(new Array(rows)).map((_, index) => (
                  <Skeleton
                    key={index}
                    variant="text"
                    animation="wave"
                    sx={{
                      bgcolor: codecolor,
                      borderRadius: 1,
                      width: `${80 - (index % 5) * 10}%`, // varying widths
                      height: 20,
                    }}
                  />
                ))}
              </Stack>
            </Box>
          </Box>
        );

      default:
        return (
          <Box sx={{ width: "100%", p: 2 }}>
            <Skeleton variant="rectangular" width="100%" height={height} animation="wave" />
          </Box>
        );
    }
  }

  // Return nothing if not open
  return null;
}